import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import {
  CommandePilotageDialogData,
  CommandePilotageItem,
  PilotageEtatBreakdown,
  PilotageStats,
  PilotageTiersBreakdown,
} from "./commande-pilotage.types";
import { exportCommandePilotageExcel } from "./commande-pilotage-excel.utils";

type SortKey =
  | "tiers"
  | "numero"
  | "etat"
  | "datePrevue"
  | "daysLate"
  | "totalHT";

@Component({
  selector: "vl-commande-pilotage-dialog",
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    DecimalPipe,
    SharedModule,
    DialogHeaderExportComponent,
    RadioCardComponent,
    AutoSignificantDigitsPipe,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: "./commande-pilotage-dialog.component.html",
  styleUrl: "./commande-pilotage-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommandePilotageDialogComponent {
  data = inject<CommandePilotageDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CommandePilotageDialogComponent>);
  private router = inject(Router);

  exporting = signal(false);

  /** Recherche libre dans le tableau de détail (tiers + numéro). */
  search = signal<string>("");

  /** Tri du tableau détail. */
  sortKey = signal<SortKey>("daysLate");
  sortDesc = signal<boolean>(true);

  toggleSort(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortDesc.set(!this.sortDesc());
    } else {
      this.sortKey.set(key);
      this.sortDesc.set(key !== "tiers" && key !== "numero" && key !== "etat");
    }
  }

  /** Libellés UI dynamiques selon le mode. */
  title = computed(() =>
    this.data.mode === "fournisseur"
      ? "Pilotage commandes fournisseurs"
      : "Pilotage commandes clients",
  );
  tiersLabel = computed(() =>
    this.data.mode === "fournisseur" ? "Fournisseur" : "Client",
  );

  /** KPIs globaux toutes étapes confondues. */
  stats = computed<PilotageStats>(() => {
    const items = this.data.items;
    const count = items.length;
    let totalHT = 0;
    let nbLate = 0;
    let totalLateHT = 0;
    let sumDaysLate = 0;
    let maxDaysLate = 0;
    for (const c of items) {
      totalHT += c.totalHT;
      if (c.daysLate > 0) {
        nbLate++;
        totalLateHT += c.totalHT;
        sumDaysLate += c.daysLate;
        if (c.daysLate > maxDaysLate) maxDaysLate = c.daysLate;
      }
    }
    const avgDaysLate = nbLate > 0 ? sumDaysLate / nbLate : 0;
    const tiers = this.tiersBreakdown();
    const topByHT =
      tiers.length > 0
        ? [...tiers].sort((a, b) => b.totalHT - a.totalHT)[0]
        : null;
    const tiersWithLate = tiers.filter((t) => t.nbLate > 0);
    const topByLate =
      tiersWithLate.length > 0
        ? [...tiersWithLate].sort((a, b) => b.nbLate - a.nbLate)[0]
        : null;
    return {
      count,
      totalHT,
      nbLate,
      totalLateHT,
      avgDaysLate,
      maxDaysLate,
      topTiersByHT: topByHT,
      topTiersByLate: topByLate,
    };
  });

  /** Breakdown par état (avec ordre métier conservé). */
  etatBreakdown = computed<PilotageEtatBreakdown[]>(() => {
    const map = new Map<string, PilotageEtatBreakdown>();
    for (const c of this.data.items) {
      let row = map.get(c.etat);
      if (!row) {
        row = {
          etat: c.etat,
          etatLabel: c.etatLabel,
          etatOrder: c.etatOrder,
          count: 0,
          totalHT: 0,
          nbLate: 0,
          pctLate: 0,
        };
        map.set(c.etat, row);
      }
      row.count++;
      row.totalHT += c.totalHT;
      if (c.daysLate > 0) row.nbLate++;
    }
    const list = [...map.values()];
    for (const r of list) r.pctLate = r.count > 0 ? r.nbLate / r.count : 0;
    return list.sort((a, b) => a.etatOrder - b.etatOrder);
  });

  /** Breakdown par tiers — top 10 par montant HT par défaut. */
  tiersBreakdown = computed<PilotageTiersBreakdown[]>(() => {
    const map = new Map<string, PilotageTiersBreakdown>();
    for (const c of this.data.items) {
      const key = c.tiersId ?? `__no_tiers_${c.tiersDenomination}`;
      let row = map.get(key);
      if (!row) {
        row = {
          tiersId: c.tiersId,
          tiersDenomination: c.tiersDenomination,
          count: 0,
          totalHT: 0,
          nbLate: 0,
          maxDaysLate: 0,
          lastCommandeDate: null,
        };
        map.set(key, row);
      }
      row.count++;
      row.totalHT += c.totalHT;
      if (c.daysLate > 0) {
        row.nbLate++;
        if (c.daysLate > row.maxDaysLate) row.maxDaysLate = c.daysLate;
      }
      const d = c.dateCreation ? new Date(c.dateCreation) : null;
      if (d && (!row.lastCommandeDate || d > new Date(row.lastCommandeDate))) {
        row.lastCommandeDate = c.dateCreation;
      }
    }
    return [...map.values()].sort((a, b) => b.totalHT - a.totalHT);
  });

  topTiers = computed(() => this.tiersBreakdown().slice(0, 10));

  /** Détail filtré par recherche + trié. */
  filteredItems = computed<CommandePilotageItem[]>(() => {
    const q = this.search().trim().toLowerCase();
    const list = q
      ? this.data.items.filter(
          (c) =>
            c.tiersDenomination.toLowerCase().includes(q) ||
            c.numero.toLowerCase().includes(q),
        )
      : [...this.data.items];
    const key = this.sortKey();
    const dir = this.sortDesc() ? -1 : 1;
    list.sort((a, b) => {
      let cmp = 0;
      switch (key) {
        case "tiers":
          cmp = a.tiersDenomination.localeCompare(b.tiersDenomination);
          break;
        case "numero":
          cmp = a.numero.localeCompare(b.numero);
          break;
        case "etat":
          cmp = a.etatOrder - b.etatOrder;
          break;
        case "datePrevue": {
          const da = a.dateLivraisonPrevue
            ? new Date(a.dateLivraisonPrevue).getTime()
            : Infinity;
          const db = b.dateLivraisonPrevue
            ? new Date(b.dateLivraisonPrevue).getTime()
            : Infinity;
          cmp = da - db;
          break;
        }
        case "daysLate":
          cmp = a.daysLate - b.daysLate;
          break;
        case "totalHT":
          cmp = a.totalHT - b.totalHT;
          break;
      }
      return cmp * dir;
    });
    return list;
  });

  formatEuro(v: number): string {
    if (!isFinite(v)) return "—";
    return v.toLocaleString("fr-FR", {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  formatPct(v: number): string {
    if (!isFinite(v)) return "—";
    return `${(v * 100).toFixed(1)} %`;
  }

  navigateToCommande(c: CommandePilotageItem): void {
    const url =
      this.data.mode === "fournisseur"
        ? `/commandes/details/id/${c.id}/commande`
        : `/commandeclient/details/id/${c.id}/commande-client`;
    this.dialogRef.close();
    this.router.navigateByUrl(url);
  }

  async exportExcel(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportCommandePilotageExcel({
        mode: this.data.mode,
        items: this.data.items,
        stats: this.stats(),
        etatBreakdown: this.etatBreakdown(),
        tiersBreakdown: this.tiersBreakdown(),
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
